import {Trans} from '@lingui/macro';
import React, {FC, Fragment, useCallback, useMemo, useState} from 'react';
import styled, {css} from 'styled-components';

import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ellipsis} from '../../../libs/shared/ui/src/styles/mixins';
import {useLocalize} from '../../../srcCommon/internationalization/internationalizationContext';
import {AccountsSetupGuideContainer} from '../../components/accounts/accountsSetupGuide/accountsSetupGuideContainer';
import {TimeFormatsEnum} from '../../helpers/dateTimeHelpers';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {AccountsSetupGuideTasksEnum} from '../../models/api/accountsSetupGuideModel';
import {CompanyPainPointsEnum} from '../../models/api/companyPreferencesModel';
import {DoNotDisturb} from '../../models/api/preferencesModel';
import {TeammateModel} from '../../models/api/teammateModel';
import {getAccountsSetupGuideCompletedTasks} from '../../store/data/localPreferences/localPreferencesSelectors';
import {AvailabilityStatus} from '../../store/data/teammates/availabilityStatusSelectors';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {settingsNavigationWidth} from '../settingsCommonConstants';
import {SettingsHomeArticle} from './settingsHomeArticle';
import {
  getArticlesColumnsFromPainPoints,
  getFullPainPointsCollection,
  getUserArticles,
} from './settingsHomeArticles';
import {SettingsHomePendingTeammateProps, SettingsHomePendingTeammates} from './settingsHomePendingTeammates';
import {SettingsHomeProfile} from './settingsHomeProfile';
import {SettingsHomeShortcut} from './settingsHomeShortcut';
import {
  getAdminHomeShortcuts,
  getUserHomeShortcuts,
  ProfileTogglesState,
  SettingsHomeDestinationsEnum,
} from './settingsHomeTypes';

/*
 * Constants.
 */

const maxAccountsSetupGuideTasks = 3;

/*
 * Style.
 */

const SettingsHomeWrapperDiv = styled.div`
  padding: 30px 20px;

  height: 100%;
  overflow: auto;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
`;

const SettingsHomeTitle = styled.h1`
  color: ${(p) => p.theme.greys.shade90};
  font-size: 26px;
  font-weight: 900;
  margin-top: 1px;
  ${ellipsis()};
`;

const SettingsHomeSubtitle = styled.h2`
  color: ${(p) => p.theme.greys.shade90};
  font-size: 22px;
  font-weight: 900;
  margin-top: 16px;
  margin-bottom: 22px;
`;

const SettingsHomeShortcutsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0;
  position: relative;
`;

const settingsHomeGridStyles = css`
  display: grid;
  grid-gap: 20px;
  padding-bottom: 14px;
  grid-template-columns: 1fr;

  @media (min-width: ${970 + settingsNavigationWidth}px) {
    grid-template-columns: repeat(2, 1fr);
    padding-left: 0;
    padding-right: 0;
  }

  @media (min-width: ${1200 + settingsNavigationWidth}px) {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 373px));
    grid-gap: 20px;
    padding-bottom: 14px;
  }
`;

const SettingsAdminHomeShorcutsWrapper = styled(SettingsHomeShortcutsWrapper)`
  ${settingsHomeGridStyles};
  padding-bottom: 20px;
`;

const SettingsAdminHomeShortcutsContainerDiv = styled.div`
  display: grid;
  grid-auto-flow: rows;
  grid-auto-rows: minmax(176px, auto);
  grid-gap: 20px;

  @media (min-width: ${970 + settingsNavigationWidth}px) {
    grid-column-end: span 2;
    grid-template-columns: repeat(2, 1fr);
  }
`;

const SettingsHomeArticlesWrapper = styled.div`
  ${settingsHomeGridStyles};
  grid-auto-rows: auto;
`;

const SettingsHomeArticlesColumn = styled.div`
  grid-column-end: span 1;

  @media (min-width: ${1200 + settingsNavigationWidth}px) {
    grid-column-end: span 2;
  }

  margin-bottom: 11px;
`;

const SettingsHomeArticlesColumnTitle = styled.h4`
  color: ${(p) => p.theme.greys.shade90};
  font-size: 16px;
  font-weight: 800;
  margin-bottom: 18px;
  margin-left: -1px;
`;

const StyledSettingsHomeMoreButtonWrapperDiv = styled.div`
  display: flex;
  justify-content: center;
  margin-top: -6px;
`;

const SettingsUserHomeShortcutsWrapperDiv = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 373px));
  grid-auto-columns: minmax(auto, 373px);
  grid-auto-rows: minmax(176px, auto);
  grid-template-areas:
    'profile shortcuts shortcuts'
    'profile shortcuts shortcuts';
  grid-gap: 20px;
  margin-bottom: 20px;

  @media (max-width: ${1200 + settingsNavigationWidth}px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-areas:
      'profile shortcuts'
      'profile shortcuts';
    padding-left: 0;
    padding-right: 0;
  }

  @media (max-width: ${970 + settingsNavigationWidth}px) {
    grid-template-columns: 1fr;
    grid-template-areas: 'profile' 'shortcuts';
    grid-template-rows: auto;
  }
`;

interface AccountsSetupGuideDivProps {
  $hasRecentTeammates: boolean;
}
const AccountsSetupGuideDiv = styled.div<AccountsSetupGuideDivProps>`
  ${(p) => addAccountsSetupGuideDivStyles(p.$hasRecentTeammates)};
  display: grid;
  grid-auto-flow: row;
`;

function addAccountsSetupGuideDivStyles(hasRecentTeammates: boolean) {
  if (hasRecentTeammates) {
    return css`
      grid-row: 3 / span 1;
      grid-column: 1 / span 1;

      @media (min-width: ${970 + settingsNavigationWidth}px) {
        grid-row: 1 / span 2;
        grid-column: 2 / span 1;
      }

      @media (min-width: ${1200 + settingsNavigationWidth}px) {
        grid-row: 2 / span 1;
        grid-column: 1 / span 2;
      }
    `;
  }

  return css`
    grid-row: 4 / span 2;
    grid-column: 1 / span 1;

    @media (min-width: ${970 + settingsNavigationWidth}px) {
      grid-row: 1 / span 3;
      grid-column: 2 / span 1;
    }

    @media (min-width: ${1200 + settingsNavigationWidth}px) {
      grid-row: 1 / span 3;
      grid-column: 4 / span 3;
    }
  `;
}

function computeAdminSettingsHomeShortcutColumnSpan(isAccountsSetupGuideVisible: boolean) {
  return isAccountsSetupGuideVisible ? 3 : 2;
}

/*
 * Types.
 */

interface SettingsHomeProps {
  panelId: SettingsPanelId;
  painPoints: ReadonlyArray<CompanyPainPointsEnum>;
  teammateProps: ReadonlyArray<SettingsHomePendingTeammateProps>;
  onArticleClick: HandlerOf<string>;
  onShortcutLinkClick: HandlerOf<SettingsHomeDestinationsEnum>;
  isGlobalAdmin: boolean;
  onProfileToggleChange: HandlerOf<ProfileTogglesState>;
  onAvatarChange: HandlerOf<File>;
  doNotDisturb: DoNotDisturb;
  shouldMuteNotifications: boolean;
  user: TeammateModel;
  userTimeFormat: TimeFormatsEnum;
  userAvailabilityStatus: AvailabilityStatus;
  shouldRenderAdmin: boolean;
  personalInboxesCount: number;
  personalAnswersCount: number;
  personalRespondersCount: number;
  personalSignaturesCount: number;
}

/*
 * Components.
 */

export const SettingsHome: FC<SettingsHomeProps> = (props) => {
  const [isShowingAll, setIsShowingAll] = useState(false);
  const showAllArticles = useCallback(() => setIsShowingAll(true), [setIsShowingAll]);

  return (
    <SettingsHomeWrapperDiv>
      {props.shouldRenderAdmin ? (
        <AdminSettingsHomeContent
          showAllArticles={isShowingAll}
          onShowMoreClick={showAllArticles}
          {...props}
        />
      ) : (
        <UserSettingsHomeContent {...props} />
      )}
    </SettingsHomeWrapperDiv>
  );
};

const UserSettingsHomeContent: FC<SettingsHomeProps> = (props) => {
  const localizer = useLocalize();
  const title = props.user.givenName ? (
    <Trans id="settingshome-welcomeuser">Welcome, {props.user.givenName}!</Trans>
  ) : (
    <Trans id="settingshome-welcome">Welcome!</Trans>
  );

  return (
    <>
      <SettingsHomeTitle>{title}</SettingsHomeTitle>
      <SettingsHomeShortcutsWrapper>
        <SettingsUserHomeShortcutsWrapperDiv>
          <SettingsHomeProfile
            userTimeFormat={props.userTimeFormat}
            userAvailabilityStatus={props.userAvailabilityStatus}
            onProfileToggleChange={props.onProfileToggleChange}
            onAvatarChange={props.onAvatarChange}
            doNotDisturb={props.doNotDisturb}
            shouldMuteNotifications={props.shouldMuteNotifications}
            user={props.user}
          />
          {getUserHomeShortcuts(
            props.personalInboxesCount,
            props.personalAnswersCount,
            props.personalRespondersCount,
            props.personalSignaturesCount,
          ).map((shortcut) => {
            const secondaryProps = shortcut.secondary
              ? {
                  secondaryLabel: shortcut.secondary.label,
                  onSecondaryClick: () => props.onShortcutLinkClick(shortcut.secondary!.destination),
                }
              : {};

            return (
              <SettingsHomeShortcut
                key={shortcut.icon}
                icon={shortcut.icon}
                title={shortcut.title}
                description={shortcut.description}
                primaryLabel={shortcut.primary.label}
                onPrimaryClick={() => props.onShortcutLinkClick(shortcut.primary.destination)}
                {...secondaryProps}
                noMarginBottom
              />
            );
          })}
        </SettingsUserHomeShortcutsWrapperDiv>
      </SettingsHomeShortcutsWrapper>
      <SettingsHomeSubtitle>
        <Trans id="settingshome-getmostoffront">Get the most out of Front</Trans>
      </SettingsHomeSubtitle>
      <SettingsHomeArticlesWrapper>
        {getUserArticles().map((article) => (
          <SettingsHomeArticlesColumn key={article.icon}>
            <SettingsHomeArticle
              icon={article.icon}
              key={article.title(localizer)}
              articleColor={article.color}
              link={article.link}
              title={article.title(localizer)}
              description={article.description(localizer)}
              onArticleClick={() => props.onArticleClick(article.title(localizer))}
            />
          </SettingsHomeArticlesColumn>
        ))}
      </SettingsHomeArticlesWrapper>
    </>
  );
};

interface SettingsHomeAdminPageProps extends SettingsHomeProps {
  onShowMoreClick: Handler;
  showAllArticles: boolean;
}

const AdminSettingsHomeContent: FC<SettingsHomeAdminPageProps> = (props) => {
  const accountsSetupGuideCompletedTasks = useAppSelector(getAccountsSetupGuideCompletedTasks);
  const columns = useMemo(() => {
    const painPoints = getFullPainPointsCollection(props.painPoints).slice(
      0,
      props.showAllArticles ? undefined : 3,
    );

    return getArticlesColumnsFromPainPoints(painPoints);
  }, [props.painPoints, props.showAllArticles]);

  const hasRecentTeammates = Boolean(props.teammateProps && props.teammateProps.length > 1);

  // Only show the Accounts Setup Guide if the guide has not been completed and if either the
  // rule or custom field task is complete.
  const hasAccount = accountsSetupGuideCompletedTasks.includes(AccountsSetupGuideTasksEnum.ADD_ACCOUNT);
  const hasAccountCustomField = accountsSetupGuideCompletedTasks.includes(
    AccountsSetupGuideTasksEnum.ADD_ACCOUNT_CUSTOM_FIELD,
  );
  const isAccountsSetupGuideCompleted = accountsSetupGuideCompletedTasks.length >= maxAccountsSetupGuideTasks;
  const isAccountsSetupGuideVisible = (hasAccount || hasAccountCustomField) && !isAccountsSetupGuideCompleted;

  const ShortcutsContainer =
    hasRecentTeammates && !isAccountsSetupGuideVisible ? SettingsAdminHomeShortcutsContainerDiv : Fragment;
  const localizer = useLocalize();

  return (
    <>
      <SettingsHomeTitle>Admin home</SettingsHomeTitle>
      <SettingsAdminHomeShorcutsWrapper>
        <ShortcutsContainer>
          {getAdminHomeShortcuts(hasRecentTeammates).map((shortcut) => {
            const secondaryProps = shortcut.secondary
              ? {
                  secondaryLabel: shortcut.secondary.label,
                  onSecondaryClick: () => props.onShortcutLinkClick(shortcut.secondary.destination),
                }
              : {};

            return (
              <SettingsHomeShortcut
                key={shortcut.icon}
                icon={shortcut.icon}
                title={shortcut.title}
                description={shortcut.description}
                primaryLabel={shortcut.primary.label}
                onPrimaryClick={() => props.onShortcutLinkClick(shortcut.primary.destination)}
                columnSpan={computeAdminSettingsHomeShortcutColumnSpan(isAccountsSetupGuideVisible)}
                {...secondaryProps}
              />
            );
          })}
        </ShortcutsContainer>
        {isAccountsSetupGuideVisible && (
          <AccountsSetupGuideDiv $hasRecentTeammates={hasRecentTeammates}>
            <AccountsSetupGuideContainer settingsPanelId={props.panelId} />
          </AccountsSetupGuideDiv>
        )}
        {hasRecentTeammates && (
          <SettingsHomePendingTeammates
            icon="group"
            title={<Trans id="settingshome-recentteammates">Recently invited teammates</Trans>}
            primaryLabel={<Trans id="settingshome-addteammates">Add teammates</Trans>}
            onPrimaryClick={() => props.onShortcutLinkClick(SettingsHomeDestinationsEnum.ADD_TEAMMATES)}
            secondaryLabel={<Trans id="settingshome-reviewteammates">Review all</Trans>}
            onSecondaryClick={() => props.onShortcutLinkClick(SettingsHomeDestinationsEnum.REVIEW_TEAMMATES)}
            teammates={props.teammateProps}
            isGlobalAdmin={props.isGlobalAdmin}
          />
        )}
      </SettingsAdminHomeShorcutsWrapper>
      <SettingsHomeSubtitle>
        <Trans id="settingshome-learnhowto">Learn how to...</Trans>
      </SettingsHomeSubtitle>
      <SettingsHomeArticlesWrapper>
        {columns.map((column) => (
          <SettingsHomeArticlesColumn key={column.title(localizer)}>
            <SettingsHomeArticlesColumnTitle>{column.title(localizer)}</SettingsHomeArticlesColumnTitle>
            {column.articles.map((article) => (
              <SettingsHomeArticle
                icon={article.icon}
                key={article.title(localizer)}
                articleColor={article.color}
                link={article.link}
                title={article.title(localizer)}
                description={article.description(localizer)}
                onArticleClick={() => props.onArticleClick(article.title(localizer))}
              />
            ))}
          </SettingsHomeArticlesColumn>
        ))}
      </SettingsHomeArticlesWrapper>
      <StyledSettingsHomeMoreButtonWrapperDiv>
        {!props.showAllArticles && (
          <Button variant={ButtonVariantsEnum.LINK} isRounded onClick={props.onShowMoreClick}>
            <Trans id="settingshome-showmore">Show more</Trans>
          </Button>
        )}
      </StyledSettingsHomeMoreButtonWrapperDiv>
    </>
  );
};
